import { isReference } from '@apollo/client';
import type { FetchResult } from '@apollo/client';

import { isDevelopment } from '@constants/env';

// import { VITAL_SIGN_TYPES } from '@constants/fhir';
import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { VitalSignObservationRecord } from '@models/vital-sign';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useDeleteBodyObservationRecordMutation,
  useGetBodyObservationQuery,
  useListBodyObservationsQuery,
  usePostBodyObservationRecordMutation,
  useUpdateBodyObservationRecordMutation,
  type DeleteBodyObservationRecordMutation,
  type DeleteBodyObservationRecordMutationVariables,
  type PostBodyObservationRecordMutationVariables,
  type UpdateBodyObservationRecordMutationVariables,
} from 'generated/body-observation.generated';

export const useGetBodyObservations = (
  code?: string,
  skipQuery?: boolean,
  filterUnpublished?: boolean,
  dateRange?: DateRange
) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListBodyObservationsQuery({
    variables: {
      input: {
        patient: patient_id,
        code,
        filterUnpublished,
        dateFrom: dateRange?.dateFrom,
        dateTo: dateRange?.dateTo,
        count: 3000,
      },
    },
    skip: !patient_id || Boolean(skipQuery),
  });
  const { data, error } = response;
  handleError(error);
  const observations = (data?.listBodyObservations.data ?? []).map(
    (o) => new VitalSignObservationRecord(o)
  );
  return { ...response, data: observations };
};

export const useGetBodyObservation = (params: {
  record_id: string;
  skip?: boolean;
}) => {
  const { handleError } = useResponseHandler();
  const { record_id, skip } = params;
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetBodyObservationQuery({
    variables: {
      input: {
        patient_id,
        record_id,
      },
    },
    skip: skip || !patient_id || !record_id,
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getBodyObservation;
  return {
    ...response,
    data: record ? new VitalSignObservationRecord(record) : null,
  };
};

export function useCreateBodyObservation(): [
  (
    params: PostBodyObservationRecordMutationVariables
  ) => Promise<FetchResult<VitalSignObservationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostBodyObservationRecordMutation();

  const loading = baseLoading;

  async function createBodyObservation(
    params: PostBodyObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postBodyObservationRecord) {
          return;
        }

        try {
          cache.modify<{ listBodyObservations: CachedRecordList }>({
            fields: {
              listBodyObservations: (existing, { storeFieldName, DELETE }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === params.input.subject?.reference) {
                  return DELETE;
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdBodyObs = response.data?.postBodyObservationRecord;
    return {
      ...response,
      data: createdBodyObs
        ? new VitalSignObservationRecord(createdBodyObs)
        : undefined,
    };
  }

  return [createBodyObservation, loading];
}

export function useUpdateBodyObservation(): [
  (
    params: UpdateBodyObservationRecordMutationVariables
  ) => Promise<FetchResult<VitalSignObservationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateBodyObservationRecordMutation();
  const loading = baseLoading;

  async function updateBodyObservation(
    params: UpdateBodyObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedBodyObs = response.data?.updateBodyObservationRecord;
    return {
      ...response,
      data: updatedBodyObs
        ? new VitalSignObservationRecord(updatedBodyObs)
        : undefined,
    };
  }
  return [updateBodyObservation, loading];
}

export function useDeleteBodyObservation(): [
  (
    params: DeleteBodyObservationRecordMutationVariables
  ) => Promise<FetchResult<DeleteBodyObservationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteBodyObservationRecordMutation();
  const loading = baseLoading;

  async function deleteBodyObservation(
    params: DeleteBodyObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteBodyObservationRecord) {
          return;
        }
        const { observation_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'ObservationModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteBodyObservation, loading];
}
